import React, { useState, useEffect, useRef } from 'react';
import { Form, Select, Input, Button, Upload, message, Carousel, Cascader, DatePicker } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import styles from './index.module.scss';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { getBannerList, getBannerTypeList } from '@/api/banner';
import { getProductCategoryAllList } from '@/api/products';
import { saveEmergencyService, getEmergencyEmailVerificationCode, saveEmergencyServiceWithVerification, getEmergencyServiceEndingSoon } from '@/api/emergencyServiceApi';
// 使用 Ant Design 内置的 dayjs

import SearchConponent from '@/components/SearchConponent/SearchConponent';
import NearbyOrders from './components/NearbyOrders/NearbyOrders';
import UrgentEndingSection from './components/UrgentEndingSection/UrgentEndingSection';
import Membership from '@/components/Membership';
import { setUserInfo } from '@/store/slices/userSlice';

import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { TextArea } = Input;

//级联选择器选项类型
interface CascaderOption {
  value: number;
  label: string;
  children?: CascaderOption[];
  isLeaf?: boolean;
}

//级联选择器选项类型
interface IClassification {
  is_active: boolean;
  is_delete: boolean;
  order: any;
  parent_id: number;
  product_category_id: number;
  product_category_name: string;
  child?: IClassification[];
}

const EmergencyService: React.FC = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  //获取token
  const token = useAppSelector((state) => state.user.token);

  //国际化语言切换
   const { t, i18n } = useTranslation();
  
  // 获取用户信息
  const userInfo = useAppSelector((state) => state.user.userInfo);

  // 是否是会员
  const [isMember, setIsMember] = useState(false);

  // 搜索框内容
  const [keyword, setKeyword] = useState<string>('');

  // 产品类别级联选择器相关状态
  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>([]);
  const [selectedCascaderValue, setSelectedCascaderValue] = useState<number[]>([]);

  // 获取定位
  const latitude = useAppSelector((state) => state.user.latitude);
  const longitude = useAppSelector((state) => state.user.longitude);

  // 美国州列表
  const StateList = useAppSelector((state) => state.common.StateList);
  // 紧急程度
  const UrgencyLevel = useAppSelector((state) => state.common.UrgencyLevel);
  // 推送费
  const PushFee = useAppSelector((state) => state.common.PushFee);
  // 服务分类数据 - 保留原有数据作为备选
  const serviceCategories = [ 
    'Appliance Repair/Install', 
    'Pipe Unclogging', 
    'Lock Repair/Replacement', 
    'Water & Electricity Repair', 
    'Digital Office', 
    'Cleaning Services', 
    'Furniture Maintenance', 
    'Tile & Flooring', 
    'Phone Repair' 
  ]; 

  // 服务分类数据 
  const [serviceCategoriesWithSub, setServiceCategoriesWithSub] = useState<IClassification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [activePrimary, setActivePrimary] = useState<IClassification | null>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [activeSecondary, setActiveSecondary] = useState<IClassification | null>(null);
  const serviceRef = useRef<HTMLDivElement | null>(null);
  // 短信验证码发送与倒计时
  const [sendingCode, setSendingCode] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);
  const countdownTimerRef = useRef<any>(null);

  // 添加一个状态来触发 NearbyOrders 组件的刷新
  const [refreshNearbyOrders, setRefreshNearbyOrders] = useState<number>(0);

  // 添加会员弹窗状态控制
  const [membershipModalOpen, setMembershipModalOpen] = useState<boolean>(false);

  // 处理会员购买弹窗的回调函数
  const handleMembershipCancel = () => {
    setMembershipModalOpen(false);
  };

  const handleMembershipConfirm = (values: any) => {
    console.log('Membership purchase confirmed:', values);
    
    // 检查是否是支付成功
    if (values.paymentSuccess) {
      message.success('Membership purchased successfully! You can now submit emergency service requests.');
      setMembershipModalOpen(false);
      setIsMember(true);

      // 支付成功后，更新 localStorage 中的 userinfo
      const userInfoStr = localStorage.getItem('userInfo');
      let userInfo = userInfoStr ? JSON.parse(userInfoStr) : {};

      // 如果有选择英里数，则把extension写入到vip_info中
      const selectedMiles = values?.selectedMiles;
      if (typeof selectedMiles === 'number') {
        const vipList = Array.isArray(userInfo.vip_info) ? userInfo.vip_info : [];
        const newVipRecord = {
          vip_type: 1, // 改为拼车会员
          extension: [{ mile: selectedMiles }],
          create_time: new Date().toISOString(),
        };
        userInfo.vip_info = [...vipList, newVipRecord];
      }

      dispatch(setUserInfo(userInfo));
      
      // 支付成功后，继续保存紧急服务
      // 获取表单数据并提交
      form.validateFields().then((formValues) => {
        handleFormSubmit(formValues, true); // 跳过会员检查
      }).catch((error) => {
        // 表单验证失败，不处理
      });
    } else {
      setMembershipModalOpen(false);
    }
  };



  //搜索回调
  const getMessage = (inputValue:string): void => {
    // dispatch(setSearchValue(inputValue));
    navigate(`/searchList?keyword=${inputValue}`);
    
  }
  // 图片上传配置
  const uploadProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".png,.jpeg,.jpg,.gif",
    headers: {
      token: `${token}`,
    },
    listType: 'picture-card',
    maxCount: 5,
    multiple: true,
    disabled: false,  
    showUploadList: {
      showPreviewIcon: false,  // 隐藏预览图标
      showRemoveIcon: true,    // 显示删除图标
      showDownloadIcon: false, // 隐藏下载图标
    },
    fileList: fileList,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("Image must be smaller than 10MB!");
      }
      return isLt10M; // 返回 false 阻止上传
    },
    onChange(info) {
      const newFileList = [...info.fileList];

      newFileList.forEach(file => {
        if (file.response) {
          file.url = file?.response?.data?.url;
        }
      });

      setFileList(newFileList);
    }
  };

  // 组件卸载时清理倒计时
  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  // 邮箱格式验证
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 发送邮箱验证码
  const handleSendCode = async () => {
    if (sendingCode || countdown > 0) return;
    const phone: string = (form.getFieldValue('phone') || '').toString();
    // const email: string = (form.getFieldValue('email') || '').toString();
    // if (!email) {
    //   message.error('Please enter email');
    //   return;
    // }
    // if (!isValidEmail(email)) {
    //   message.error('Invalid email format');
    //   return;
    // }
    console.log('Sending email code - email check skipped',form.getFieldValue('phone'));
    
    try {
      setSendingCode(true);
      const res: any = await getEmergencyEmailVerificationCode({ phone });
      if (res && res.code === 200) {
        message.success('Verification code sent to your phone');
        setCountdown(60);
        countdownTimerRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownTimerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        message.error(res?.msg || 'Failed to send code');
      }
    } catch (e) {
      message.error('Failed to send code');
    } finally {
      setSendingCode(false);
    }
  };

  // 紧急服务横幅轮播
  const [bannerList, setBannerList] = useState<any[]>([]);
  const [bannerTypeList, setBannerTypeList] = useState<any[]>([]);
  // 广告位列表（purpose = 2）
  const [adList, setAdList] = useState<any[]>([]);

  useEffect(() => {
    getBannerTypeList({ purpose: 1, path: location.pathname }).then((res: any) => {
      if (res.code == 200) {
        setBannerTypeList(res.data);
        const bannerType = res.data.find((item: any) => item.path == location.pathname);

        if (bannerType) {
          // http://localhost:3000/api/web/get_banner_list_by_type?banner_type=home&purpose=2 传purpose=2查广告位 传purpose=1查轮播
          getBannerList({ path: location.pathname, purpose: 1 }).then((res2: any) => {
            if (res2.code == 200) {
              setBannerList(res2.data || []);
            }
          });
          // 获取广告位（purpose = 2）
          getBannerList({ path: location.pathname, purpose: 2 }).then((res3: any) => {
            if (res3.code == 200) {
              setAdList(res3.data || []);
            }
          });
        }
      }
    });
  }, [location.pathname]);

  // 将原始分类数据转换为Cascader需要的格式
  const transformToCascaderOptions = (data: IClassification[] = []): CascaderOption[] => {
    return data.map(item => {
      const hasChildren = item.child && item.child.length > 0;
      return {
        value: item.product_category_id,
        label: item.product_category_name,
        children: hasChildren ? transformToChildrenOptions(item.child) : undefined,
        isLeaf: !hasChildren // 如果没有子分类，则标记为叶子节点
      };
    });
  };

  // 转换子分类数据
  const transformToChildrenOptions = (children: IClassification[] = []): CascaderOption[] => {
    return children.map(child => {
      const hasChildren = child.child && child.child.length > 0;
      return {
        value: child.product_category_id,
        label: child.product_category_name,
        children: hasChildren ? transformToChildrenOptions(child.child) : undefined,
        isLeaf: !hasChildren // 如果没有子分类，则标记为叶子节点
      };
    });
  };

  // 显示分类路径
  const displayRender = (labels: string[]): string => {
    return labels.join(' > ');
  };

  // 处理分类选择
  const handleCascaderChange = (value: any, selectedOptions: any): void => {
    console.log(value, 'value');
    console.log(selectedOptions, 'selectedOptions');

    if (selectedOptions && selectedOptions.length > 0) {
      const lastOption = selectedOptions[selectedOptions.length - 1];
      // 只有当选择的是叶子节点时才设置值
      if (lastOption.isLeaf) {
        // 保存完整路径
        setSelectedCascaderValue(value);
        // 设置表单值
        form.setFieldValue('serviceCategory', value);

      }
    }
  };

  // 初始化获取分类数据
  useEffect(() => {
    setLoading(true);
    // 获取商品分类列表
    // product_category_type_id 1商品 2服务商品
    getProductCategoryAllList(2).then((res) => {
      console.log(res, 'getProductCategoryAllList');
      if (res.code === 200) {
        const data = res.data || [];
        //格式化分类数据
        const options = transformToCascaderOptions(data);
        setCascaderOptions(options);
        // 同时设置服务分类列表
        setServiceCategoriesWithSub(data);
      } else {
        message.error('获取服务分类失败，请重试');
      }
    }).catch((error) => {
      console.error('获取服务分类失败:', error);
      message.error('获取服务分类失败，请重试');
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    // 判断是否是会员 - 检查拼车会员(vip_type=1)
    if (userInfo && Array.isArray(userInfo.vip_info)) {
      const carpoolVips = (userInfo.vip_info as any[]).filter(
        (vip: any) => vip?.vip_type === 1 && !vip?.is_delete
      );
      if (carpoolVips.length > 0) {
        setIsMember(true);
      }
    }
  },[userInfo]);

  // 分类点击导航（用于二级、三级）
  const handleCategoryNavigate = (cat: IClassification) => {
    if (!cat) return;
    const name = cat.product_category_name || '';
    navigate(`/serviceSearchList?keyword=${encodeURIComponent(name)}`);
    // 关闭面板
    setActivePrimary(null);
  };

  // 当一级切换时，默认选中第一个二级
  useEffect(() => {
    if (activePrimary && activePrimary.child && activePrimary.child.length > 0) {
      setActiveSecondary(activePrimary.child[0]);
    } else {
      setActiveSecondary(null);
    }
  }, [activePrimary]);

  const handleFormSubmit = async (values: any, skipMembershipCheck: boolean = false) => {
    // 如果不是跳过会员检查，先检查是否是会员
    if (!skipMembershipCheck) {
      // 检查是否是会员
      if (!isMember) {
        setMembershipModalOpen(true);
        return;
      }
    }
    if (!values.verificationCode) {
      message.error('Please enter verification code');
      return;
    }
     
    // 处理预约时间格式 - 转换为字符串
    const formattedAppointmentTime = values.appointmentTime ? values.appointmentTime.format('YYYY-MM-DD HH:mm:ss') : '';
    
    // 处理图片数据格式
    const formattedImages = fileList.map(file => ({
      product_id: null,
      product_media_type: "images",
      product_media_file_url: file.url || '',
      product_media_video_cover_id: null,
      file_id: file.uid,
      product_media_id: null
    }));
    
    // 获取经纬度
    const key = process.env.REACT_APP_GOOGLE_MAP_KEY as string;
    const addr = values.address as string;
    
    let latitude: number | null = null;
    let longitude: number | null = null;
    try {
      if (!addr || !key) {
        message.error('地址或地图密钥缺失');
        return;
      }
      
      const addressVariations = [
        addr,
        `${addr}, USA`,
        `${addr}, United States`,
        `${addr}, ${values.cityState && typeof values.cityState === 'string' ? getStateName(values.cityState) : ''}`
      ].filter(Boolean);
      
      let geoJson = null;
      for (const address of addressVariations) {
        try {
          const geoRes = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${key}`);
          geoJson = await geoRes.json();
          
          if (geoJson.status === 'OK' && geoJson.results && geoJson.results[0]) {
            break; // 找到有效结果，跳出循环
          }
        } catch (e) {
        }
      }

       if (geoJson && geoJson.status === 'OK' && geoJson.results && geoJson.results[0]) {
         const loc = geoJson.results[0].geometry.location;
         // 保留小数点后五位，超过则四舍五入
         longitude = Math.round(loc.lng * 100000) / 100000;
         latitude = Math.round(loc.lat * 100000) / 100000;
       } 
    } catch (e) {
      return;
    }
    
    // 组装提交参数
     const submitData = {
      //  email: email, // string: 邮箱
       code: values.verificationCode, // string: 验证码
       service_category: values.serviceCategory, // 数组
       problem_description: values.problemDescription, // string: 问题描述
       budget: Number(values.budget), // number: 预算金额
       fixed_price: Number(values.fixedPrice), // number: 固定价格
       city_state: values.cityState, // string: 城市/州ID
       address: values.address, // string: 详细地址
       phone: values.phone || '', // string: 联系电话（可选）
       appointment_time: formattedAppointmentTime, // string: 预约时间 (YYYY-MM-DD HH:mm:ss)
       images: formattedImages, // array: 图片数组
       latitude, // number: 纬度
       longitude, // number: 经度
       urgency_level: values.urgency_level, // string: 紧急程度
       push_fee: values.push_fee, // string: 推送费
     };
    
   // 提交到后端（使用邮箱验证码接口）
   try {
     const res: any = await saveEmergencyServiceWithVerification(submitData);
     if (res && res.code === 200) {
      message.success(res?.data?.message || 'Emergency service published successfully!');
       form.resetFields();
       setFileList([]);
       setSelectedCascaderValue([]);
       // 提交成功后刷新 NearbyOrders 组件
       setRefreshNearbyOrders(prev => prev + 1);
       
       // 清除验证码倒计时
       if (countdownTimerRef.current) {
         clearInterval(countdownTimerRef.current);
         countdownTimerRef.current = null;
       }
       setCountdown(0);
       const productsToOrder = [{
        product_id: "PROD-3b56545e11561764313798029",
        type: 1,  // 1预付款  2尾款
        product_sku_id: "SN-763c990a-9fec-4fc0-847e-9b909d8435df",
       }];
       localStorage.setItem("emergency_service_info",JSON.stringify(res.data.data))
      //  const productsToOrder = res?.data?.data;
       
       const encodedData = btoa(JSON.stringify(productsToOrder));
       console.log(res,111111111,productsToOrder,encodedData)
       navigate(`/emergency_service_ordering?products_to_order=${encodedData}`);
     } 
   } catch (error) {
        }
    };
  
    // 根据州ID获取州名
    const getStateName = (stateId: string): string => {
      const state = StateList.find(item => item.value === stateId);
      return state ? state.label : '';
    };
  
    return (
    <div>
     <SearchConponent keyword={keyword} getMessage={getMessage} />
      <div className={styles.emergencyServiceContainer}>
        {/* 顶部区域 */}
        <div className={styles.topSection}>
          {/* 左侧主内容区域 */}
          <div className={styles.leftContent}>
            {/* 顶部行 */}
            <div className={styles.topRow}>
              {/* 左上角 */}
              <div
                ref={serviceRef}
                className={styles.serviceClassification}
                onMouseLeave={() => {
                  setActivePrimary(null);
                }}
              >
                <h3>Service Classification</h3>
                  <div className={styles.categoryContainer}>
                       <ul className={styles.primaryList}>
                         {serviceCategoriesWithSub.map((category) => (
                           <li
                             key={category.product_category_id}
                             className={styles.primaryItem}
                             onMouseEnter={(e) => {
                               setActivePrimary(category);
                               const box = serviceRef.current?.getBoundingClientRect();
                               const itemRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                               const top = box ? box.top : itemRect.top;
                               const left = itemRect.right + 10;
                               setPanelPos({ top, left });
                             }}
                             onClick={() => handleCategoryNavigate(category)}
                           >
                             {category.product_category_name}
                           </li>
                         ))}
                       </ul>

                       {activePrimary && (
                         <div
                           className={styles.megaPanel}
                           style={{ top: panelPos.top, left: panelPos.left }}
                         >
                           {(activePrimary.child || []).map((sec) => (
                             <div key={sec.product_category_id} className={styles.megaColumn}>
                               <div
                                 className={styles.megaTitle}
                                 onClick={() => handleCategoryNavigate(sec)}
                               >
                                 <span>{sec.product_category_name} :</span>
                               </div>
                               <div className={styles.megaLinks}>
                                 {(sec.child || []).map((third, index) => (
                                   <React.Fragment key={third.product_category_id}>
                                     <span
                                       className={styles.megaLink}
                                       onClick={() => handleCategoryNavigate(third)}
                                     >
                                       {third.product_category_name}
                                     </span>
                                     {index < ((sec.child || []).length - 1) && <span>&nbsp;/&nbsp;</span>}
                                   </React.Fragment>
                                 ))}
                               </div>
                             </div>
                           ))}
                         </div>
                       )}
                 </div>
              </div>

              {/* 右上角 */}
              <div className={styles.emergencyBanner}>
                <div className={styles.bannerContent}>
                  <Carousel autoplay >
                    {bannerList.map((item, index) => (
                      <div key={index} onClick={() => navigate(item.linkUrl || '/') } style={{ width: '100%', height: '100%' }}>
                        <img
                          src={item.imageUrl}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    ))}
                  </Carousel>
                </div>
              </div>
            </div>

            {/* 底部行 */}
            <div className={styles.bottomRow}>
              <div className={styles.servicePreviews}>
                {(adList && adList.length > 0 ? adList : []).map((item: any) => (
                  <div
                    key={item.id || item.title}
                    className={styles.servicePreviewCard}
                    onClick={() => item.linkUrl && navigate(item.linkUrl)}
                    style={{ cursor: (item.linkUrl ? 'pointer' : 'default') }}
                  >
                    <div className={styles.serviceImage}>
                      <img src={item.imageUrl || item.image} alt={item.title} />
                    </div>
                    <div className={styles.serviceInfo}>
                      <h1 style={{ color:'white',fontSize:'16px'}}>{item.title}</h1>
                      <p>Click To View Details</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Urgent Ending 区域 */}
            <div style={{ marginTop: '30px' }}>
              <UrgentEndingSection 
                user_latitude={latitude || undefined} 
                user_longitude={longitude || undefined} 
              />
            </div>
          </div>

          {/* 右侧：订单发布表单 */}
          <div className={styles.orderForm}>
            <h3>{t('EmergencyService.OrderRelease')}</h3>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFormSubmit}
            >
              <Form.Item name="serviceCategory" rules={[{ required: true,message:t('EmergencyService.FieldRequired')}]}> 
                <Cascader
                  className={styles.serviceCategoryField}
                  value={selectedCascaderValue.length > 0 ? selectedCascaderValue : undefined}
                  options={cascaderOptions}
                  expandTrigger="hover"
                  displayRender={displayRender}
                  onChange={handleCascaderChange}
                  placeholder={t('EmergencyService.SelectCategory')}
                 
                  changeOnSelect={false}
                  // 只允许选择叶子节点
                  fieldNames={{ label: 'label', value: 'value', children: 'children' }}
                  showSearch={{
                    filter: (inputValue, path) =>
                      path.some(option =>
                        option.label.toLowerCase().includes(inputValue.toLowerCase())
                      ),
                    matchInputWidth: true,
                    limit: 10,
                  }}
                />
              </Form.Item>
              <Form.Item name="urgency_level" rules={[{ required: true,message:t('EmergencyService.FieldRequired') }]}> 
                <Select 
                  placeholder={t('membershipChild.UrgencyLevel')}
                  allowClear
                >
                  {UrgencyLevel.map((item, index) => (
                    <Option key={index} value={item.value}>{item.label}</Option>
                ))}
                </Select>
              </Form.Item>
              <Form.Item name="push_fee" rules={[{ required: true,message:t('EmergencyService.FieldRequired') }]}> 
                <Select 
                  placeholder={t('membershipChild.PushFee')}
                  allowClear
                >
                  {PushFee.map((item, index) => (
                    <Option key={index} value={item.value}>{item.label}</Option>
                ))}
                </Select>
              </Form.Item>
              <Form.Item name="cityState" rules={[{ required: true,message:t('EmergencyService.FieldRequired') }]}> 
                <Select 
                  placeholder={t('membershipChild.PleaseInputYourState')}
                  allowClear
                >
                  {StateList.map((item, index) => (
                    <Option key={index} value={item.value}>{item.label}</Option>
                ))}
                </Select>
              </Form.Item>
              <Form.Item name="address" rules={[{ required: true,message:t('EmergencyService.FieldRequired') }]}> 
                <Input 
                  placeholder={t('membershipChild.Address')}
                  allowClear 
                />
              </Form.Item>
              {/* 删除 Service Type 表单项 */}

              <Form.Item name="problemDescription" rules={[{ required: true,message:t('EmergencyService.FieldRequired') }]}> 
                <Input 
                  placeholder={t('EmergencyService.ProblemDescription')} 
                  allowClear
                />
              </Form.Item>

              <Form.Item name="images" label={t('EmergencyService.UploadImages')} rules={[{ required: true,message:t('EmergencyService.FieldRequired') }]}>
                <Upload {...uploadProps} className={styles.customUpload} style={{width: '100px',height: '50px'}}>
                  {fileList.length >= 5 ? null : t('EmergencyService.ClickToUpload')}
                </Upload>
              </Form.Item>

              <Form.Item name="budget" rules={[{ required: true,message:t('EmergencyService.FieldRequired') }]}> 
                <Input 
                  placeholder={t('EmergencyService.EnterBudget')}
                  inputMode="numeric" 
                  pattern="[0-9]*" 
                  allowClear
                  onKeyPress={(e) => {
                    // 只允许输入数字 //Enter Budget (E.G., $100)
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    e.target.value = value;
                  }}
                />
              </Form.Item>
              <Form.Item name="appointmentTime" rules={[{ required: true,message:t('EmergencyService.FieldRequired') }]}> 
                <DatePicker 
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder={t('EmergencyService.SelectAppointmentTime')}
                  style={{ width: '100%' }}
                />
              </Form.Item>             
              <Form.Item name="phone" rules={[{ required: false }]}> 
                <Input 
                  placeholder={t('membershipChild.Phone') + ' (Optional)'}
                  type="tel" 
                  inputMode="tel" 
                  maxLength={20} 
                  allowClear 
                />
              </Form.Item>

              <Form.Item name="verificationCode"> 
                <div className={styles.verificationRow}>
                  <Input 
                    placeholder={t('EmergencyService.EnterVerifyCode')}
                    inputMode="numeric" 
                    pattern="[0-9]*" 
                    maxLength={6} 
                  />
                  <Button className={styles.sendCodeBtn} onClick={handleSendCode} disabled={sendingCode || countdown > 0}>
                    {countdown > 0 ? `${countdown}s` : t('EmergencyService.SendVerifyCode')}
                  </Button>
                </div>
              </Form.Item>
              {/*"Please Select An Appointment Time"*/}
              <Form.Item>
                <Button type="primary" htmlType="submit" className={styles.publishBtn}>
                  {t('EmergencyService.PublishImmediately')}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
      <NearbyOrders serviceCategories={cascaderOptions} refresh={refreshNearbyOrders} />
      
      <Membership 
        open={membershipModalOpen}
        onCancel={handleMembershipCancel}
        onConfirm={handleMembershipConfirm}
        membershipType={0}
      />
    </div>
  );
};

export default EmergencyService;  